import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Play, Loader2, AlertTriangle, CheckCircle2, TrendingUp, Download, FileText, Users, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReadinessCheckOrchestrator = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [orchestrating, setOrchestrating] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    fetchLatestResults();
  }, [project.id]);

  const fetchLatestResults = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/readiness-check/latest`);
      setResults(response.data.results);
    } catch (error) {
      // No results yet
      console.log('No readiness check results yet');
    } finally {
      setLoading(false);
    }
  };

  const runOrchestration = async () => {
    setOrchestrating(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/readiness-check/orchestrate`, {});
      setResults(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setOrchestrating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-12 h-12 text-blue-600 animate-spin" /></div>;

  const getRAGColor = (status) => {
    switch(status) {
      case 'GREEN': return 'bg-green-500';
      case 'AMBER': return 'bg-yellow-500';
      case 'RED': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Readiness Check Orchestrator</h2>
            <p className="text-gray-600">Unified migration readiness with SAP RC + AURA insights</p>
            
            {/* SAP System Indicator */}
            {results && results.sap_system_analyzed && results.sap_system_analyzed.environment_name && (
              <div className="mt-3 flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                  <Shield className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700">Analyzing:</span>
                  <span className="font-bold text-blue-600">{results.sap_system_analyzed.environment_name}</span>
                  <span className="text-gray-500">({results.sap_system_analyzed.host})</span>
                </div>
                {results.data_source === 'REAL_SAP_SYSTEM' && (
                  <div className="px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <span className="text-green-800 font-semibold flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>📊 Real Data</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
            >
              <FileText className="w-5 h-5" />
              <span>Upload SAP RC File</span>
            </button>
            <button onClick={runOrchestration} disabled={orchestrating} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50">
              {orchestrating ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Orchestrating...</span></> : <><Play className="w-5 h-5" /><span>Run Readiness Check</span></>}
            </button>
          </div>
        </div>
        
        {uploadedFile && (
          <div className="mt-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-gray-900">SAP Readiness Check File Uploaded</p>
                <p className="text-xs text-gray-600">{uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(2)} KB)</p>
              </div>
            </div>
            <button
              onClick={() => setUploadedFile(null)}
              className="text-sm text-red-600 hover:text-red-700 font-semibold"
            >
              Remove
            </button>
          </div>
        )}
      </div>

      {!results && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Shield className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Readiness Check Results</h3>
          <p className="text-gray-600 mb-6">Run the orchestrator to analyze migration readiness</p>
          <button onClick={runOrchestration} className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 inline-flex items-center space-x-2">
            <Play className="w-5 h-5" />
            <span>Start Readiness Check</span>
          </button>
        </div>
      )}

      {results && (
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'scores', label: 'Scores & Heatmap' },
                { id: 'blockers', label: 'Top Blockers' },
                { id: 'backlog', label: 'Backlog' },
                { id: 'roadmap', label: 'Roadmap' },
                { id: 'executive', label: 'Executive Summary' }
              ].map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-3 font-medium transition-all ${activeTab === tab.id ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === 'overview' && <OverviewTab results={results} getRAGColor={getRAGColor} />}
              {activeTab === 'scores' && <ScoresTab results={results} getRAGColor={getRAGColor} />}
              {activeTab === 'blockers' && <BlockersTab results={results} />}
              {activeTab === 'backlog' && <BacklogTab results={results} />}
              {activeTab === 'roadmap' && <RoadmapTab results={results} />}
              {activeTab === 'executive' && <ExecutiveTab results={results} getRAGColor={getRAGColor} />}
            </div>
          </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={(file) => setUploadedFile(file)}
        />
      )}
    </div>
  );
};

const OverviewTab = ({ results, getRAGColor }) => (
  <div className="space-y-6">
    <div className={`rounded-xl p-8 text-white shadow-xl ${getRAGColor(results.readiness_scores.rag_status)}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-3xl font-bold mb-2">Migration Readiness</h3>
          <p className="text-lg opacity-90">{results.readiness_scores.rag_label}</p>
        </div>
        <div className="text-7xl font-bold">{results.readiness_scores.global_score}%</div>
      </div>
      <div className="mt-6 grid grid-cols-4 gap-4">
        <div className="bg-white/20 backdrop-blur rounded-lg p-3">
          <div className="text-2xl font-bold">{results.summary.total_items}</div>
          <div className="text-sm opacity-80">Total Items</div>
        </div>
        <div className="bg-white/20 backdrop-blur rounded-lg p-3">
          <div className="text-2xl font-bold">{results.summary.total_blockers}</div>
          <div className="text-sm opacity-80">Blockers</div>
        </div>
        <div className="bg-white/20 backdrop-blur rounded-lg p-3">
          <div className="text-2xl font-bold">{results.summary.estimated_timeline_months}m</div>
          <div className="text-sm opacity-80">Timeline</div>
        </div>
        <div className="bg-white/20 backdrop-blur rounded-lg p-3">
          <div className="text-2xl font-bold">{results.summary.total_effort_days}d</div>
          <div className="text-sm opacity-80">Effort</div>
        </div>
      </div>
    </div>

    <div className="grid md:grid-cols-4 gap-6">
      {Object.entries(results.readiness_scores.domain_scores).map(([domain, score]) => (
        <div key={domain} className="bg-white border-2 border-gray-200 rounded-xl p-6">
          <div className="text-center">
            <div className={`w-20 h-20 mx-auto mb-3 rounded-full flex items-center justify-center ${
              score >= 75 ? 'bg-green-100' : score >= 50 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <span className={`text-2xl font-bold ${
                score >= 75 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600'
              }`}>{score}%</span>
            </div>
            <div className="font-semibold text-gray-900">{domain}</div>
          </div>
        </div>
      ))}
    </div>

    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-xl font-bold mb-4">Readiness Summary</h3>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Scoring Weights Applied</h4>
          <div className="space-y-2 text-sm">
            {Object.entries(results.readiness_scores.weights_applied).map(([key, weight]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                <span className="font-semibold">{(weight * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Item Status</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Total Items:</span><span className="font-semibold">{results.readiness_scores.total_items}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Open Items:</span><span className="font-semibold text-orange-600">{results.readiness_scores.open_items}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Closed Items:</span><span className="font-semibold text-green-600">{results.readiness_scores.closed_items}</span></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ScoresTab = ({ results, getRAGColor }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-xl font-bold mb-6">Domain Readiness Heatmap</h3>
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(results.readiness_scores.domain_scores).map(([domain, score]) => (
          <div key={domain} className={`rounded-lg p-6 text-white text-center ${
            score >= 75 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
          }`}>
            <div className="text-4xl font-bold mb-2">{score}%</div>
            <div className="text-sm opacity-90">{domain}</div>
          </div>
        ))}
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-xl font-bold mb-6">Module Readiness Scores</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(results.readiness_scores.module_scores).map(([module, score]) => (
          <div key={module} className="border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-900">{module}</span>
              <div className="flex items-center space-x-2">
                <span className="text-xl font-bold text-gray-900">{score}%</span>
                <div className={`w-3 h-3 rounded-full ${
                  score >= 75 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
              </div>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div className={`h-2 rounded-full ${
                score >= 75 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
              }`} style={{width: `${score}%`}} />
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-xl font-bold mb-6">Severity Distribution</h3>
      <div className="grid grid-cols-5 gap-4">
        {Object.entries(results.readiness_scores.severity_distribution).map(([severity, count]) => (
          <div key={severity} className="text-center">
            <div className={`rounded-lg p-6 mb-2 ${
              severity === 'Critical' ? 'bg-red-100' :
              severity === 'High' ? 'bg-orange-100' :
              severity === 'Medium' ? 'bg-yellow-100' :
              'bg-blue-100'
            }`}>
              <div className={`text-3xl font-bold ${
                severity === 'Critical' ? 'text-red-600' :
                severity === 'High' ? 'text-orange-600' :
                severity === 'Medium' ? 'text-yellow-600' :
                'text-blue-600'
              }`}>{count}</div>
            </div>
            <div className="text-sm text-gray-600">{severity}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const BlockersTab = ({ results }) => (
  <div className="space-y-6">
    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
      <div className="flex items-center space-x-3 mb-4">
        <AlertTriangle className="w-8 h-8 text-red-600" />
        <div>
          <h3 className="text-2xl font-bold text-gray-900">Top 10 Migration Blockers</h3>
          <p className="text-gray-600">Critical items requiring immediate attention</p>
        </div>
      </div>
    </div>

    <div className="space-y-3">
      {results.migration_blockers.map((blocker, idx) => (
        <div key={idx} className="bg-white border-2 border-red-200 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">{idx + 1}</div>
                <h4 className="text-lg font-bold text-gray-900">{blocker.title}</h4>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  blocker.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                  'bg-orange-100 text-orange-700'
                }`}>{blocker.severity}</span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">{blocker.remediation_priority}</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{blocker.impact_description}</p>
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <span>Category: {blocker.category}</span>
                <span>Module: {blocker.module}</span>
                <span>Effort: {blocker.effort}</span>
                <span>Source: {blocker.source}</span>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <h5 className="font-semibold text-gray-900 mb-2">Blocker Reasons:</h5>
            <ul className="space-y-1">
              {blocker.reasons.map((reason, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start space-x-2">
                  <span className="text-yellow-600">•</span>
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const BacklogTab = ({ results }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold">Migration Backlog by Workstream</h3>
        <div className="text-sm text-gray-600">
          Total: {results.migration_backlog.total_backlog_items} items | {results.migration_backlog.total_effort_days} days
        </div>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        {Object.entries(results.migration_backlog.workstreams).map(([workstream, data]) => (
          <div key={workstream} className="bg-white border-2 border-gray-200 rounded-xl p-6">
            <div className="text-center mb-4">
              <h4 className="font-bold text-gray-900 mb-2">{workstream}</h4>
              <div className="text-3xl font-bold text-blue-600">{data.total_items}</div>
              <div className="text-xs text-gray-500 mt-1">items</div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-gray-600">P0:</span><span className="font-semibold text-red-600">{data.p0_items}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">P1:</span><span className="font-semibold text-orange-600">{data.p1_items}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">P2:</span><span className="font-semibold text-yellow-600">{data.p2_items}</span></div>
              <div className="flex justify-between border-t pt-2 mt-2"><span className="text-gray-600">Effort:</span><span className="font-semibold">{data.total_effort_days}d</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {Object.entries(results.migration_backlog.workstreams).filter(([_, data]) => data.total_items > 0).map(([workstream, data]) => (
      <div key={workstream} className="bg-white rounded-xl shadow-md p-6">
        <h4 className="text-lg font-bold mb-4">{workstream} Workstream ({data.total_items} items)</h4>
        <div className="space-y-2">
          {data.items.slice(0, 5).map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    item.priority === 'P0' ? 'bg-red-100 text-red-700' :
                    item.priority === 'P1' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>{item.priority}</span>
                  <span className="font-semibold text-sm">{item.title}</span>
                  <span className="text-xs text-gray-500">{item.module}</span>
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-700">{item.estimated_days}d</span>
            </div>
          ))}
          {data.items.length > 5 && (
            <div className="text-center text-sm text-gray-500 pt-2">
              + {data.items.length - 5} more items
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);

const RoadmapTab = ({ results }) => (
  <div className="space-y-6">
    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold mb-2">Migration Roadmap</h3>
          <p className="text-blue-100">High-level timeline with critical path</p>
        </div>
        <div className="text-right">
          <div className="text-4xl font-bold mb-1">{results.migration_roadmap.total_duration_months}</div>
          <div className="text-sm opacity-80">Months</div>
        </div>
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-md p-6">
      <h4 className="text-lg font-bold mb-4">Roadmap Phases</h4>
      <div className="space-y-4">
        {results.migration_roadmap.phases.map((phase, idx) => (
          <div key={idx} className="border-2 border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-semibold text-gray-900">{phase.phase}</h5>
              <span className="text-lg font-bold text-blue-600">{phase.duration_weeks} weeks</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-600">Start:</span> <span className="font-semibold">{new Date(phase.start_date).toLocaleDateString()}</span></div>
              <div><span className="text-gray-600">End:</span> <span className="font-semibold">{new Date(phase.end_date).toLocaleDateString()}</span></div>
              <div><span className="text-gray-600">Effort:</span> <span className="font-semibold">{phase.effort_days} days</span></div>
              <div><span className="text-gray-600">Workstreams:</span> <span className="font-semibold">{phase.workstreams.join(', ')}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-md p-6">
      <h4 className="text-lg font-bold mb-4">Timeline Details</h4>
      <div className="space-y-3">
        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
          <span className="font-semibold text-gray-900">Earliest Go-Live Ready Date:</span>
          <span className="text-lg font-bold text-green-600">{new Date(results.migration_roadmap.earliest_go_live_date).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
          <span className="font-semibold text-gray-900">Critical Path Workstream:</span>
          <span className="text-lg font-bold text-blue-600">{results.migration_roadmap.critical_path_workstream}</span>
        </div>
        <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
          <span className="font-semibold text-gray-900">Parallel Workstreams:</span>
          <span className="text-lg font-bold text-purple-600">{results.migration_roadmap.parallel_workstreams.join(', ')}</span>
        </div>
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-md p-6">
      <h4 className="text-lg font-bold mb-4">Assumptions</h4>
      <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-gray-600">Team Capacity:</span><span className="font-semibold">{results.migration_roadmap.assumptions.team_capacity_days_per_week} days/week</span></div>
        <div className="flex justify-between"><span className="text-gray-600">Concurrent Workstreams:</span><span className="font-semibold">{results.migration_roadmap.assumptions.concurrent_workstreams}</span></div>
        <div className="flex justify-between"><span className="text-gray-600">Buffer:</span><span className="font-semibold">{results.migration_roadmap.assumptions.buffer_percentage}%</span></div>
      </div>
    </div>
  </div>
);

const ExecutiveTab = ({ results, getRAGColor }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-600">
      <h3 className="text-xl font-bold mb-4">Executive Summary (CIO View)</h3>
      <div className="prose max-w-none">
        <div className="whitespace-pre-line text-gray-700">{results.ai_recommendations.executive_summary}</div>
      </div>
    </div>

    <div className="grid md:grid-cols-3 gap-6">
      <div className={`rounded-xl p-6 text-white ${getRAGColor(results.readiness_scores.rag_status)}`}>
        <Shield className="w-10 h-10 mb-3 opacity-80" />
        <div className="text-3xl font-bold mb-2">{results.readiness_scores.global_score}%</div>
        <div className="text-sm opacity-90">Readiness Score</div>
      </div>
      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
        <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
        <div className="text-3xl font-bold mb-2">{results.summary.total_blockers}</div>
        <div className="text-sm opacity-90">Critical Blockers</div>
      </div>
      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
        <Calendar className="w-10 h-10 mb-3 opacity-80" />
        <div className="text-3xl font-bold mb-2">{results.summary.estimated_timeline_months}</div>
        <div className="text-sm opacity-90">Months Timeline</div>
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-md p-6">
      <h4 className="text-lg font-bold mb-4">Recommended Work Packages</h4>
      <div className="space-y-3">
        {results.ai_recommendations.work_packages.map((pkg, idx) => (
          <div key={idx} className="border-2 border-blue-200 bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold text-gray-900">{pkg.package_name}</h5>
              <span className="text-lg font-bold text-blue-600">{pkg.estimated_weeks}w</span>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>{pkg.items_count} items</span>
              <span>Owner: {pkg.owner}</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-md p-6">
      <h4 className="text-lg font-bold mb-4">Quick Wins (Low Effort, High Impact)</h4>
      <div className="space-y-2">
        {results.ai_recommendations.quick_wins.map((win, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-green-50 border-2 border-green-200 rounded-lg">
            <div>
              <h5 className="font-semibold text-gray-900 text-sm">{win.title}</h5>
              <p className="text-xs text-gray-600">{win.recommendation}</p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm font-semibold text-green-600">{win.effort}</span>
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// File Upload Modal
const UploadModal = ({ onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('json');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Auto-detect file type
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (ext === 'json') setFileType('json');
      else if (ext === 'xml') setFileType('xml');
      else if (ext === 'csv') setFileType('csv');
    }
  };

  const handleUpload = () => {
    if (file) {
      onUpload(file);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Upload SAP Readiness Check Results</h3>
        
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">How to Get SAP Readiness Check Results:</h4>
          <ol className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">1.</span>
              <span>Log into your SAP ECC system</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">2.</span>
              <span>Execute transaction <code className="bg-white px-2 py-1 rounded">SAP_RC</code> or access via SAP Support Portal</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">3.</span>
              <span>Run Readiness Check for your target S/4HANA release</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">4.</span>
              <span>Export results as <strong>JSON</strong> (preferred), XML, or CSV</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">5.</span>
              <span>Upload the exported file here</span>
            </li>
          </ol>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Select File Format</label>
          <select
            value={fileType}
            onChange={(e) => setFileType(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
          >
            <option value="json">JSON (Recommended)</option>
            <option value="xml">XML</option>
            <option value="csv">CSV</option>
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Upload SAP Readiness Check File</label>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
            <input
              type="file"
              accept=".json,.xml,.csv"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 mb-2">
                {file ? (
                  <span className="text-blue-600 font-semibold">{file.name}</span>
                ) : (
                  <>Click to browse or drag and drop</>
                )}
              </p>
              <p className="text-xs text-gray-500">Supported: .json, .xml, .csv</p>
            </label>
          </div>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Note:</strong> If you don't have SAP Readiness Check results, AURA will use discovery data from Landscape Discovery Engine. 
            For best results, upload actual SAP RC output.
          </p>
        </div>

        <div className="flex justify-end space-x-4">
          <button type="button" onClick={onClose} className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50"
          >
            Upload & Process
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReadinessCheckOrchestrator;
